/**
 * Data layer for the Real-Time GEX workspace (components/livegex).
 *
 * Mirrors what the MMEC TradingView overlay does (its src/api.js), so the vendored level
 * engine (vendor/mmecOverlay/processor.js) sees the same rows:
 *   stable chain (0DTE pins today's expiry by name) + accumulated volume merged in from
 *   source=rtl_legacy ("Bid/Ask Volume accumelated"), keyed by expiry|strike|type.
 * Auth is added by the global fetch wrapper (utils/installApiAuthHeaders).
 */
import { getApiUrl } from "../config/api";

const FEED: Record<string, string> = { SPX: "SPXW", NDX: "NDXP", RUT: "RUTW" };

/** Options root the chain is keyed by (SPX → SPXW …). */
export function feedSymbol(symbol: string): string {
    const s = String(symbol || "").toUpperCase();
    return FEED[s] || s;
}

export type LiveGexRange = "0dte" | "week";

async function getJson(path: string, timeoutMs = 150000): Promise<any> {
    const ctrl = new AbortController();
    const timer = window.setTimeout(() => ctrl.abort(), timeoutMs);
    try {
        const res = await fetch(getApiUrl(path), { signal: ctrl.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status} ${path.split("?")[0]}`);
        return await res.json();
    } finally {
        window.clearTimeout(timer);
    }
}

function extractRows(data: any): any[] {
    if (Array.isArray(data)) return data;
    if (!data) return [];
    if (Array.isArray(data.rows)) return data.rows;
    if (Array.isArray(data.data)) return data.data;
    if (data.options && Array.isArray(data.options)) return data.options;
    if (data.result) return extractRows(data.result);
    return [];
}

function num(v: any): number {
    if (v == null || v === "" || v === "-") return 0;
    const n = typeof v === "number" ? v : parseFloat(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : 0;
}

/** "YYYY-MM-DD" (stable) or "MM/DD/YY" (rtl_legacy) → "YYYYMMDD". */
export function expiryKey(raw: any): string {
    const s = String(raw || "").trim();
    let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return `${m[1]}${m[2]}${m[3]}`;
    m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
    if (m) {
        let y = +m[3];
        if (y < 100) y += 2000;
        return `${y}${String(m[1]).padStart(2, "0")}${String(m[2]).padStart(2, "0")}`;
    }
    return s.replace(/[^0-9]/g, "");
}

export function todayEtYmd(now: Date = new Date()): string {
    return now.toLocaleDateString("en-CA", { timeZone: "America/New_York" }).replace(/-/g, "");
}

const typeOf = (row: any): "C" | "P" =>
    String(row["Option Type"] ?? row.option_type ?? "").toUpperCase().startsWith("C") ? "C" : "P";

/** All-day RTL rows for one options root (bid/ask accumulated volume, delta, premium). */
export async function fetchRtlRows(feed: string): Promise<any[]> {
    const q = new URLSearchParams({ source: "rtl_legacy", underlyings: feed, strike_range: "200" });
    return extractRows(await getJson(`/api/accumulated-data?${q}`, 60000));
}

/** Give each stable row its accumulated volume so the engine can derive VGF / VMAX / VMIN. */
export function attachAccumulatedVolume(rows: any[], rtlRows: any[]): number {
    const acc = new Map<string, number>();
    for (const r of rtlRows) {
        const strike = num(r["Strike"] ?? r.strike);
        if (!strike) continue;
        const exp = expiryKey(r["Expiry Date"] ?? r.expiry_date ?? r.expiry);
        acc.set(
            `${exp}|${strike}|${typeOf(r)}`,
            num(r["Bid Volume accumelated"] ?? r["Bid Volume accumulated"]) +
                num(r["Ask Volume accumelated"] ?? r["Ask Volume accumulated"]),
        );
    }
    let attached = 0;
    for (const row of rows) {
        const exp = expiryKey(row["Expiry Date"] ?? row.expiry_date ?? row.expiry);
        const v = acc.get(`${exp}|${num(row["Strike"] ?? row.strike)}|${typeOf(row)}`);
        if (v != null) {
            row["Accumulated Volume"] = v;
            attached++;
        }
    }
    return attached;
}

export interface ChainResult {
    rows: any[];
    /** Expiry the rows belong to (YYYYMMDD) — the nearest one for the weekly range. */
    expiryYmd: string | null;
    /** Days to that expiry; null when the rows span several expiries (weekly range). */
    dte: number | null;
}

/** 0DTE falls back to the nearest expiry, but only this close — keeps the view on 0–5 DTE. */
export const NEAREST_MAX_DTE = 5;

const rowExpiry = (r: any): string => expiryKey(r["Expiry Date"] ?? r.expiry_date ?? r.expiry);

function daysBetweenYmd(fromYmd: string, toYmd: string): number {
    const d = (s: string) => Date.UTC(+s.slice(0, 4), +s.slice(4, 6) - 1, +s.slice(6, 8));
    return Math.round((d(toYmd) - d(fromYmd)) / 86400000);
}

async function stableRows(symbol: string, expiryRange: string, expiryDate?: string): Promise<any[]> {
    const q = new URLSearchParams({
        source: "stable",
        fields: "lite",
        underlyings: feedSymbol(symbol),
        strike_range: "30",
        expiry_range: expiryRange,
    });
    if (expiryDate) q.set("expiry_date", expiryDate);
    return extractRows(await getJson(`/api/accumulated-data?${q}`));
}

/**
 * Stable chain rows for the engine.
 *   0dte  today's expiry, asked for by name. Weekly-expiry names (NVDA, GLD …) have none most
 *         days, so it falls back to the NEAREST expiry within NEAREST_MAX_DTE days.
 *   week  every expiry in the weekly range, aggregated.
 */
export async function fetchChain(symbol: string, range: LiveGexRange): Promise<ChainResult> {
    const today = todayEtYmd();
    if (range === "week") {
        const rows = await stableRows(symbol, "weekly");
        const exps = Array.from(new Set(rows.map(rowExpiry).filter((e) => e >= today))).sort();
        return { rows, expiryYmd: exps[0] || null, dte: null };
    }
    const todayRows = (await stableRows(symbol, "0dte", today)).filter((r) => rowExpiry(r) === today);
    if (todayRows.length) return { rows: todayRows, expiryYmd: today, dte: 0 };
    const weekly = await stableRows(symbol, "weekly");
    const exps = Array.from(new Set(weekly.map(rowExpiry).filter((e) => e >= today))).sort();
    if (!exps.length) return { rows: [], expiryYmd: null, dte: null };
    const dte = daysBetweenYmd(today, exps[0]);
    if (dte > NEAREST_MAX_DTE) return { rows: [], expiryYmd: null, dte: null };
    return { rows: weekly.filter((r) => rowExpiry(r) === exps[0]), expiryYmd: exps[0], dte };
}

export interface SpotCheck {
    ok: boolean;
    chainSpot: number;
    spreadPct: number | null;
    parityForward: number | null;
    devFromParityPct: number | null;
    reasons: string[];
}

/**
 * The stable chain can carry a stale "Underlying Price" (rows that have not refreshed keep
 * the prior close, and one response can mix stale and live stamps). Levels computed on that
 * are anchored to the wrong price, so the workspace withholds them when this fails.
 */
export function spotCheck(rows: any[], spot: number, tolPct = 0.3): SpotCheck {
    const ups = rows.map((r) => num(r["Underlying Price"] ?? r.underlying_price)).filter((v) => v > 0);
    const spreadPct = ups.length ? ((Math.max(...ups) - Math.min(...ups)) / Math.max(...ups)) * 100 : null;
    const exps = Array.from(
        new Set(rows.map((r) => expiryKey(r["Expiry Date"] ?? r.expiry_date ?? r.expiry)).filter(Boolean)),
    ).sort();
    let parityForward: number | null = null;
    if (exps.length) {
        const byStrike = new Map<number, { c?: number; p?: number }>();
        for (const r of rows) {
            if (expiryKey(r["Expiry Date"] ?? r.expiry_date ?? r.expiry) !== exps[0]) continue;
            const k = num(r["Strike"] ?? r.strike);
            const mid = num(r["Mid"] ?? r.mid) || (num(r["Bid"] ?? r.bid) + num(r["Ask"] ?? r.ask)) / 2;
            if (!(k > 0) || !(mid > 0)) continue;
            const o = byStrike.get(k) || {};
            if (typeOf(r) === "C") o.c = mid;
            else o.p = mid;
            byStrike.set(k, o);
        }
        let best: { d: number; f: number } | null = null;
        byStrike.forEach((o, k) => {
            if (o.c == null || o.p == null) return;
            const d = Math.abs(o.c - o.p);
            if (!best || d < best.d) best = { d, f: k + o.c - o.p };
        });
        if (best) parityForward = Number((best as { d: number; f: number }).f.toFixed(2));
    }
    const devFromParityPct =
        parityForward && spot > 0 ? (Math.abs(spot - parityForward) / parityForward) * 100 : null;
    const reasons: string[] = [];
    if (spreadPct != null && spreadPct > tolPct) reasons.push(`mixed price stamps in the chain (${spreadPct.toFixed(2)}%)`);
    if (devFromParityPct != null && devFromParityPct > tolPct)
        reasons.push(`chain price ${spot.toFixed(2)} vs parity ${parityForward} (${devFromParityPct.toFixed(2)}%)`);
    return { ok: reasons.length === 0, chainSpot: spot, spreadPct, parityForward, devFromParityPct, reasons };
}

export interface RawBar {
    t: number;
    o: number;
    h: number;
    l: number;
    c: number;
    v?: number;
}

/** 5-minute bars. Index symbols come back on the ETF scale; the caller rescales to the index. */
export async function fetchBars(symbol: string): Promise<RawBar[]> {
    const q = new URLSearchParams({ symbol, interval: "5m" });
    const d = await getJson(`/api/historical/equity-bars?${q}`, 30000);
    const bars: RawBar[] = (d?.bars || []).filter((b: any) => b && b.c && b.t);
    bars.sort((a, b) => a.t - b.t);
    return bars;
}

/** Regime / IV snapshot (flat scalars): spot, iv, iv_rank, hv*, fc_*, net_gex_bn, gex_regime … */
export async function fetchIvSnapshot(symbol: string): Promise<any | null> {
    const d = await getJson(`/api/iv-tracking/tv?${new URLSearchParams({ symbol })}`, 90000);
    return d && d.ok !== false ? d : null;
}

/** Columnar 5-minute IV history: { t[], iv[], rv_intraday[], … }. */
export async function fetchIvHistory(symbol: string, days = 3): Promise<any | null> {
    const to = Math.floor(Date.now() / 1000);
    const q = new URLSearchParams({ symbol, interval: "5m", from: String(to - days * 86400), to: String(to) });
    const d = await getJson(`/api/iv-tracking/tv/history?${q}`, 90000);
    return d && d.ok !== false ? d : null;
}

export interface FlowPrint {
    side: "BUY" | "SELL" | "MIX";
    cp: "C" | "P";
    strike: number;
    volume: number;
    premium: number;
    /** days to expiry of the print's contract */
    dte: number;
}

/** Largest prints expiring today for one options root. A single stock rarely has a same-day
 *  expiry, so when there are none the session's largest prints at any expiry are returned. */
export async function fetchZeroDteFlow(feed: string, limit = 9): Promise<FlowPrint[]> {
    // largest first, from the server: a page of the newest prints is not the biggest ones
    const base = { ticker: feed, underlying: feed, limit: "40", min_premium: "25000", sort_by: "premium", sort_dir: "desc" };
    const today = extractRows(await getJson(`/api/options-flow/flow?${new URLSearchParams({ ...base, max_dte: "0" })}`, 60000)).filter(
        (r) => num(r["DTE"]) === 0 && r["DTE"] != null,
    );
    const rows = today.length ? today : extractRows(await getJson(`/api/options-flow/flow?${new URLSearchParams(base)}`, 60000));
    return rows
        .sort((a, b) => num(b["Premium"]) - num(a["Premium"]))
        .slice(0, limit)
        .map((r) => {
            const side = String(r["Bid_Ask"] ?? r["Side"] ?? "").toLowerCase();
            return {
                side: side === "ask" ? "BUY" : side === "bid" ? "SELL" : "MIX",
                cp: typeOf(r),
                strike: num(r["Strike"]),
                volume: num(r["Volume"]),
                premium: num(r["Premium"]),
                dte: num(r["DTE"]),
            } as FlowPrint;
        });
}

/** Net RTL flow per strike for one expiry: (ask − bid contracts), calls minus puts. */
export function rtlNetByStrike(rtlRows: any[], expiryYmd: string): Map<number, number> {
    const out = new Map<number, number>();
    for (const r of rtlRows) {
        if (expiryKey(r["Expiry Date"] ?? r.expiry_date ?? r.expiry) !== expiryYmd) continue;
        const k = num(r["Strike"] ?? r.strike);
        if (!k) continue;
        out.set(k, (out.get(k) || 0) + (typeOf(r) === "C" ? 1 : -1) * num(r["Delta accumelated"]));
    }
    return out;
}
